import React from 'react';
import styled from 'styled-components';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import { formatInt } from './orderTokens';

/**
 * Faixa de totais do evento, no mesmo recorte de período da lista.
 *
 * Vem de `/financial/transaction-summary` — a mesma fonte dos cards do
 * Financeiro — para que os números nunca discordem entre as duas telas. Cada
 * bloco mapeia um campo da API; nada aqui é somado no front, justamente porque
 * um total calculado na página divergiria assim que a paginação entrasse.
 *
 * "Não convertido" e "Estornado" só aparecem quando existem: uma faixa cheia de
 * R$ 0,00 gasta a atenção do produtor com problema que ele não tem.
 */
export default function EventOrdersSummary({ data, loading }) {
    const paidCount = Number(data?.count_total_transacted) || 0;
    const transacted = Number(data?.total_transacted) || 0;
    const pending = Number(data?.total_transactions_pending) || 0;
    const notConverted = Number(data?.total_transactions_canceled) || 0;
    const refunded = Number(data?.total_transactions_refunded) || 0;

    const tiles = [
        {
            key: 'paid',
            label: 'Pedidos pagos',
            value: formatInt(paidCount),
            hint: 'No período filtrado',
            tone: 'neutral',
        },
        {
            key: 'transacted',
            label: 'Total transacionado',
            value: currency.format(transacted),
            hint: 'O que os compradores pagaram',
            tone: 'strong',
        },
        {
            key: 'pending',
            label: 'Aguardando pagamento',
            value: currency.format(pending),
            hint: 'Pedido criado, dinheiro não entrou',
            tone: 'waiting',
        },
    ];

    if (notConverted > 0) {
        tiles.push({
            key: 'notConverted',
            label: 'Não convertido',
            value: currency.format(notConverted),
            hint: 'Expirado ou recusado',
            tone: 'lost',
        });
    }

    if (refunded > 0) {
        tiles.push({
            key: 'refunded',
            label: 'Estornado',
            value: currency.format(refunded),
            hint: 'Devolvido ao comprador',
            tone: 'lost',
        });
    }

    return (
        <Strip $loading={loading} aria-busy={loading}>
            {tiles.map(tile => (
                <Tile key={tile.key}>
                    <TileLabel>{tile.label}</TileLabel>
                    <TileValue $tone={tile.tone}>{tile.value}</TileValue>
                    <TileHint>{tile.hint}</TileHint>
                </Tile>
            ))}
        </Strip>
    );
}

const toneColor = {
    neutral: colors.nightDark,
    strong: '#157F3C',
    waiting: '#8A5300',
    lost: '#8A2B2B',
};

const Strip = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
    opacity: ${({ $loading }) => $loading ? 0.55 : 1};
    transition: opacity 0.15s ease;

    @media screen and (min-width: ${breakpoints.md}) {
        gap: 12px;
    }
`;

const Tile = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 14px 16px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    background: ${colors.neutralLight};
    min-width: 0;
`;

const TileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const TileValue = styled.strong`
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ $tone }) => toneColor[$tone] || colors.nightDark};
    font-variant-numeric: tabular-nums;
    word-break: break-word;
`;

const TileHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    line-height: 1.35;
`;
